import {
  ApiOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloudServerOutlined,
  ClusterOutlined,
  OrderedListOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Segmented,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { Dayjs } from 'dayjs';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAdminNotifications,
  useDashboardStats,
  useJobs,
  useMarkNotificationsRead,
  useModelGroups,
  useProviders,
  useQueueStats,
  useWorkers,
  type StatsRange,
} from '../api/hooks';
import type { JobListItem, UsageStat } from '../api/types';
import { JobStatusTag } from '../utils/status';
import { formatDateTime } from '../utils/format';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const RANGE_OPTIONS = [
  { value: '24h', label: '24 giờ' },
  { value: '3d', label: '3 ngày' },
  { value: '7d', label: '7 ngày' },
  { value: '30d', label: '1 tháng' },
  { value: 'custom', label: 'Tùy chỉnh' },
];

type StatTone = 'blue' | 'green' | 'red' | 'amber' | 'slate';

/** Thẻ thống kê: badge icon tròn + nhãn + số lớn (theo bản thiết kế mẫu). */
function StatCard({
  icon,
  label,
  value,
  suffix,
  tone = 'blue',
  loading,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  suffix?: ReactNode;
  tone?: StatTone;
  loading?: boolean;
  onClick?: () => void;
}) {
  const toneClass = tone === 'blue' ? '' : ` gw-stat__icon--${tone}`;
  return (
    <Card hoverable={!!onClick} onClick={onClick} styles={{ body: { padding: 20 } }}>
      <div className="gw-stat">
        <div className={`gw-stat__icon${toneClass}`}>{icon}</div>
        <div className="gw-stat__body">
          <span className="gw-stat__label">{label}</span>
          {loading ? (
            <Spin size="small" />
          ) : (
            <span className="gw-stat__value">
              {value}
              {suffix != null && <span className="gw-stat__suffix"> {suffix}</span>}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Quản lý trạng thái bộ lọc thời gian (preset hoặc khoảng tùy chỉnh). */
function useRangeFilter() {
  const [preset, setPreset] = useState<string>('24h');
  const [custom, setCustom] = useState<[Dayjs, Dayjs] | null>(null);
  const params = useMemo<StatsRange>(() => {
    if (preset === 'custom' && custom) {
      return { since: custom[0].toISOString(), until: custom[1].toISOString() };
    }
    return { range: preset };
  }, [preset, custom]);
  return { preset, setPreset, custom, setCustom, params };
}

function RangeFilter({ filter }: { filter: ReturnType<typeof useRangeFilter> }) {
  return (
    <Space wrap>
      <Segmented
        value={filter.preset}
        options={RANGE_OPTIONS}
        onChange={(v) => filter.setPreset(v as string)}
      />
      {filter.preset === 'custom' && (
        <RangePicker
          showTime
          value={filter.custom ?? undefined}
          onChange={(v) => filter.setCustom(v as [Dayjs, Dayjs] | null)}
        />
      )}
    </Space>
  );
}

/** Bảng xếp hạng "nguồn" hoặc "model" được dùng nhiều nhất. */
function UsageTable({
  data,
  loading,
  unitTitle,
}: {
  data: UsageStat[];
  loading: boolean;
  unitTitle: string;
}) {
  const max = data.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  return (
    <Table<UsageStat>
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={data}
      pagination={false}
      locale={{ emptyText: <Empty description="Chưa có dữ liệu" /> }}
      columns={[
        { title: unitTitle, dataIndex: 'name', key: 'name', render: (v: string) => v || '—' },
        {
          title: 'Lượt dùng',
          dataIndex: 'count',
          key: 'count',
          width: 220,
          render: (v: number) => (
            <Space style={{ width: '100%' }}>
              <div
                style={{
                  height: 8,
                  width: Math.max(8, Math.round((v / max) * 140)),
                  background: '#2563eb',
                  borderRadius: 4,
                }}
              />
              <Text strong>{v}</Text>
            </Space>
          ),
        },
      ]}
    />
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const groups = useModelGroups();
  const providers = useProviders();
  const workers = useWorkers();
  const queue = useQueueStats(10_000);

  const queueFilter = useRangeFilter();
  const usageFilter = useRangeFilter();
  const queueStats = useDashboardStats(queueFilter.params);
  const usageStats = useDashboardStats(usageFilter.params);

  const recentErrors = useJobs('failed', '', '', 1, 10);

  // Thông báo admin (vd model con vào cooldown) — chỉ hiện cái chưa đọc.
  const notifications = useAdminNotifications(true);
  const markRead = useMarkNotificationsRead();

  const onlineWorkers = (workers.data ?? []).filter((w) => w.status === 'online').length;

  const columns = [
    {
      title: 'Model / Nhóm',
      key: 'target',
      render: (_: unknown, r: JobListItem) =>
        r.model?.name ?? r.modelGroup?.name ?? r.capability?.name ?? '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (s: JobListItem['status']) => <JobStatusTag status={s} />,
    },
    {
      title: 'Tạo lúc',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
    {
      title: 'Hoàn tất lúc',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (v: string) => formatDateTime(v),
    },
  ];

  const errorColumns = [
    {
      title: 'Model / Nhóm',
      key: 'target',
      render: (_: unknown, r: JobListItem) =>
        r.model?.name ?? r.modelGroup?.name ?? r.capability?.name ?? '—',
    },
    {
      title: 'Lỗi',
      key: 'error',
      render: (_: unknown, r: JobListItem) =>
        r.errorMessage ? (
          <Text type="danger" ellipsis style={{ maxWidth: 320 }}>
            {r.errorClass ? <Tag color="red">{r.errorClass}</Tag> : null}
            {r.errorMessage}
          </Text>
        ) : (
          <Tag color="red">{r.errorClass ?? 'failed'}</Tag>
        ),
    },
    {
      title: 'Lúc',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 160,
      render: (v: string, r: JobListItem) => formatDateTime(v || r.createdAt),
    },
  ];

  return (
    <div>
      <Title level={3} className="gw-page-title">
        Tổng quan
      </Title>

      {(notifications.data?.items ?? []).length > 0 && (
        <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size={8}>
          {(notifications.data?.items ?? []).map((n) => (
            <Alert
              key={n.id}
              type={n.level === 'error' ? 'error' : n.level === 'info' ? 'info' : 'warning'}
              showIcon
              message={
                <Space>
                  <span style={{ fontWeight: 600 }}>{n.title}</span>
                  <Text type="secondary" style={{ fontWeight: 400 }}>
                    {formatDateTime(n.createdAt)}
                  </Text>
                </Space>
              }
              description={n.message ?? undefined}
              action={
                <Button size="small" onClick={() => markRead.mutate([n.id])} loading={markRead.isPending}>
                  Đã xử lý
                </Button>
              }
            />
          ))}
          {(notifications.data?.unreadCount ?? 0) > 1 && (
            <Button size="small" type="link" onClick={() => markRead.mutate(undefined)}>
              Đánh dấu tất cả đã đọc ({notifications.data?.unreadCount})
            </Button>
          )}
        </Space>
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<ClusterOutlined />}
            label="Nhóm model"
            value={groups.data?.length ?? 0}
            loading={groups.isLoading}
            onClick={() => navigate('/model-groups')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<ApiOutlined />}
            label="Nhà cung cấp"
            value={providers.data?.length ?? 0}
            loading={providers.isLoading}
            onClick={() => navigate('/providers')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<CloudServerOutlined />}
            label="Worker trực tuyến"
            value={onlineWorkers}
            suffix={`/ ${workers.data?.length ?? 0}`}
            loading={workers.isLoading}
            onClick={() => navigate('/workers')}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={<SyncOutlined spin />}
            label="Đang xử lý"
            value={queue.data?.active ?? 0}
            loading={queue.isLoading}
            onClick={() => navigate('/queue')}
          />
        </Col>
      </Row>

      <Space
        style={{ width: '100%', justifyContent: 'space-between', marginTop: 24, marginBottom: 16 }}
        wrap
      >
        <Title level={4} style={{ margin: 0 }}>
          Hàng đợi
        </Title>
        <RangeFilter filter={queueFilter} />
      </Space>
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={8} lg={4}>
          <StatCard
            icon={<ClockCircleOutlined />}
            tone="slate"
            label="Đang chờ"
            value={queueStats.data?.queue.waiting ?? 0}
            loading={queueStats.isLoading}
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard
            icon={<SyncOutlined />}
            label="Đang xử lý"
            value={queueStats.data?.queue.active ?? 0}
            loading={queueStats.isLoading}
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard
            icon={<CheckCircleOutlined />}
            tone="green"
            label="Hoàn tất"
            value={queueStats.data?.queue.completed ?? 0}
            loading={queueStats.isLoading}
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard
            icon={<CloseCircleOutlined />}
            tone="red"
            label="Thất bại"
            value={queueStats.data?.queue.failed ?? 0}
            loading={queueStats.isLoading}
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard
            icon={<StopOutlined />}
            tone="slate"
            label="Đã hủy"
            value={queueStats.data?.queue.cancelled ?? 0}
            loading={queueStats.isLoading}
          />
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <StatCard
            icon={<OrderedListOutlined />}
            label="Tổng"
            value={queueStats.data?.queue.total ?? 0}
            loading={queueStats.isLoading}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={14}>
          <Card
            title="Công việc gần đây"
            extra={
              <Button type="link" size="small" onClick={() => navigate('/jobs')}>
                Xem tất cả
              </Button>
            }
          >
            <RecentJobs columns={columns} />
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            title="Lịch sử lỗi"
            extra={
              <Button
                type="link"
                size="small"
                danger
                onClick={() => navigate('/jobs?status=failed')}
              >
                Xem chi tiết
              </Button>
            }
          >
            <Table<JobListItem>
              rowKey="id"
              size="small"
              loading={recentErrors.isLoading}
              dataSource={recentErrors.data?.items ?? []}
              columns={errorColumns}
              pagination={false}
              locale={{ emptyText: <Empty description="Không có lỗi gần đây" /> }}
              onRow={() => ({
                onClick: () => navigate('/jobs?status=failed'),
                style: { cursor: 'pointer' },
              })}
            />
          </Card>
        </Col>
      </Row>

      <Space
        style={{ width: '100%', justifyContent: 'space-between', marginTop: 24, marginBottom: 16 }}
        wrap
      >
        <Title level={4} style={{ margin: 0 }}>
          Thống kê sử dụng
        </Title>
        <RangeFilter filter={usageFilter} />
      </Space>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Nguồn (nhà cung cấp) được dùng">
            <UsageTable
              data={usageStats.data?.bySource ?? []}
              loading={usageStats.isLoading}
              unitTitle="Nhà cung cấp"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Model / Nhóm model được dùng">
            <UsageTable
              data={usageStats.data?.byModel ?? []}
              loading={usageStats.isLoading}
              unitTitle="Model / Nhóm"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

/** Bảng công việc gần đây (8 job mới nhất). */
function RecentJobs({ columns }: { columns: any[] }) {
  const navigate = useNavigate();
  const recentJobs = useJobs('', '', '', 1, 8);
  return (
    <Table<JobListItem>
      rowKey="id"
      size="small"
      loading={recentJobs.isLoading}
      dataSource={recentJobs.data?.items ?? []}
      columns={columns}
      pagination={false}
      onRow={() => ({ onClick: () => navigate('/jobs'), style: { cursor: 'pointer' } })}
    />
  );
}
